/* eslint-disable max-lines */
import {
  MenuDropdown,
  MenuItem,
  SaveIcon,
  ZONE_TYPE_CONFIGS,
  SpecialZoneType,
} from '@hakimo-ui/hakimo/ui-elements';
import { Button } from '@hakimo-ui/shared/ui-base';
import {
  ArrowPathIcon,
  ChevronUpIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { memo } from 'react';

interface Props {
  visibleZoneTypes: Set<SpecialZoneType>;
  onToggleZoneType: (type: SpecialZoneType) => void;
  onAddZone: (type: SpecialZoneType) => void;
  selectedZoneId: string | null;
  selectedZoneType?: SpecialZoneType;
  onResetZone: () => void;
  onSaveZone: () => void;
  onDeleteZone: () => void;
  isSaving?: boolean;
  canUpdateDeadZones: boolean;
  canUpdateVehicleParkingZone: boolean;
  canUpdateActiveZone: boolean;
}

export const ZoneToolbar = memo(function ZoneToolbar({
  visibleZoneTypes,
  onToggleZoneType,
  onAddZone,
  selectedZoneId,
  selectedZoneType,
  onResetZone,
  onSaveZone,
  onDeleteZone,
  isSaving,
  canUpdateDeadZones,
  canUpdateVehicleParkingZone,
  canUpdateActiveZone,
}: Props) {
  const hasAnyZonePermission =
    canUpdateDeadZones || canUpdateVehicleParkingZone || canUpdateActiveZone;

  const canEditSelectedZone =
    selectedZoneType === SpecialZoneType.DeadZone
      ? canUpdateDeadZones
      : selectedZoneType === SpecialZoneType.VehicleParkingZone
      ? canUpdateVehicleParkingZone
      : selectedZoneType === SpecialZoneType.ActiveZone
      ? canUpdateActiveZone
      : false;

  const menuItems: MenuItem[] = ZONE_TYPE_CONFIGS.filter((config) => {
    if (config.type === SpecialZoneType.DeadZone) return canUpdateDeadZones;
    if (config.type === SpecialZoneType.VehicleParkingZone)
      return canUpdateVehicleParkingZone;
    if (config.type === SpecialZoneType.ActiveZone) return canUpdateActiveZone;
    return false;
  }).map((config) => ({
    name: config.toolbarLabel,
    onClick: () => onAddZone(config.type),
    className: config.textColor,
  }));

  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="flex items-center gap-2">
        {ZONE_TYPE_CONFIGS.map((config) => {
          const isActive = visibleZoneTypes.has(config.type);
          return (
            <button
              key={config.type}
              onClick={() => onToggleZoneType(config.type)}
              className={clsx(
                'flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all',
                config.borderColor,
                isActive
                  ? [config.activeBgColor, config.activeTextColor]
                  : ['dark:bg-dark-bg bg-white', config.textColor]
              )}
            >
              <span
                className={clsx(
                  'flex h-4 w-4 items-center justify-center rounded-full border',
                  config.borderColor
                )}
              >
                {isActive && (
                  <svg
                    className="h-3 w-3"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M2 6l3 3 5-6" />
                  </svg>
                )}
              </span>
              {config.toolbarLabel}
            </button>
          );
        })}
      </div>
      {canEditSelectedZone && selectedZoneId && (
        <div className="flex items-center gap-1">
          <Button variant="icon" onClick={onResetZone} title="Reset zone">
            <ArrowPathIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
          </Button>
          <Button
            variant="icon"
            onClick={onSaveZone}
            title="Save zone"
            loading={isSaving}
          >
            <SaveIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
          </Button>
          <Button variant="icon" onClick={onDeleteZone} title="Delete zone">
            <TrashIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-5 w-5" />
          </Button>
        </div>
      )}

      {hasAnyZonePermission && (
        <MenuDropdown
          items={menuItems}
          position="top"
          menuButton={
            <Button
              variant="icon"
              className="bg-primary-500 hover:!bg-primary-600 flex gap-1 !text-white"
            >
              <PlusIcon className="h-5 w-5" />
              <ChevronUpIcon className="h-4 w-4" />
            </Button>
          }
          useOnlyMenuButtonNode
        />
      )}
    </div>
  );
});

export default ZoneToolbar;
